import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { 
  Calendar as CalendarIcon, 
  Clock, 
  ChevronRight, 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Building,
  Video,
  Home,
  User,
  Check,
  X
} from 'lucide-react'
import { 
  format, 
  startOfWeek, 
  addDays, 
  subDays, 
  isSameDay, 
  isBefore,
  isAfter,
  startOfDay 
} from 'date-fns'
import { fr } from 'date-fns/locale'
import api from '../services/api'

const STEPS = {
  TYPE: 0,
  PROFILE: 1,
  REASON: 2,
  DATETIME: 3,
  INFO: 4,
  CONFIRM: 5
}

const STEP_LABELS = ['Mode', 'Profil', 'Motif', 'Date', 'Infos', 'Vérification']

const PHONE_REGEX = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function BookingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const initialType = searchParams.get('type')
  const [step, setStep] = useState(initialType ? STEPS.PROFILE : STEPS.TYPE)
  const [loading, setLoading] = useState(false)
  const [reasons, setReasons] = useState([])
  const [publicSettings, setPublicSettings] = useState({ waitlist_enabled: true })
  const [profile, setProfile] = useState({})
  
  const[availableSlots, setAvailableSlots] = useState([])
  const [crossSellSlots, setCrossSellSlots] = useState([]) // Créneaux alternatifs
  
  // Remplacement de la navigation par semaine par un "Saut" dans le temps
  const[searchStartDate, setSearchStartDate] = useState(new Date())
  const[visibleDaysCount, setVisibleDaysCount] = useState(6) // On affiche 6 jours dispo à la fois
  
  // Accordéon (jours ouverts)
  const [expandedDates, setExpandedDates] = useState([])
  const[showCrossSellForDate, setShowCrossSellForDate] = useState([]) // Contrôle l'encart alternatif

  // États de la Liste d'Attente

  const[waitlistModalOpen, setWaitlistModalOpen] = useState(false)
  const[waitlistData, setWaitlistData] = useState({ first_name: '', last_name: '', phone: '', message: '' })
  const [submittingWaitlist, setSubmittingWaitlist] = useState(false)

  const { register, handleSubmit, watch, setValue, trigger, formState: { errors } } = useForm({
    mode: 'onChange',
    defaultValues: {
      consultation_type: initialType || '',
      patient_type: 'new',
      reason_id: '',
      reason_other: '',
      date: '',
      start_time: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      notes: ''
    }
  })

  const watchType = watch('consultation_type')
  const watchPatientType = watch('patient_type')
  const watchReasonId = watch('reason_id') // On surveille spécifiquement l'ID
  const watchDate = watch('date')
  const watchStartTime = watch('start_time')

  // --- LOGIQUE D'INTÉGRATION PORTAIL PATIENT ---
  const { token } = useParams()
  const isPortal = !!token

  useEffect(() => {
    api.get('/api/admin/reasons').then(res => setReasons(res.data))
    api.get('/api/settings/public').then(res => setPublicSettings(res.data)).catch(() => {})
    api.get('/api/workspaces/public-profile').then(res => setProfile(res.data)).catch(() => {})
    
    // Si on est dans le portail, on pré-remplit les coordonnées du patient !
    if (isPortal) {
        api.get(`/api/portal/${token}/dashboard`).then(res => {
            const p = res.data.patient;
            setValue('first_name', p.first_name);
            setValue('last_name', p.last_name);
            setValue('email', p.email || '');
            setValue('phone', p.phone || '');
            // On le force en patient de suivi
            setValue('patient_type', 'follow_up');
        }).catch(() => toast.error("Impossible de pré-remplir vos données."));
    }
  },[isPortal, token, setValue])

  // On cherche sur 30 jours au lieu de 7 !

  useEffect(() => {
    if (watchType && watchPatientType) {
      const fromDate = format(searchStartDate, 'yyyy-MM-dd')
      const toDate = format(addDays(searchStartDate, 30), 'yyyy-MM-dd')
      
      setLoading(true)
      
      const requests =[
          api.get('/api/slots/available', {
              params: { consultation_type: watchType, patient_type: watchPatientType, from_date: fromDate, to_date: toDate }
          })
      ]

      // CROSS-SELLING Bi-directionnel
      const crossType = watchType === 'cabinet' ? 'video' : (watchType === 'video' ? 'cabinet' : null);

      if (crossType) {
          requests.push(
              api.get('/api/slots/available', {
                  params: { consultation_type: crossType, patient_type: watchPatientType, from_date: fromDate, to_date: toDate }
              })
          )
      } else {
          setCrossSellSlots([])
      }

      Promise.all(requests)
      .then(responses => {
          setAvailableSlots(responses[0].data)
          if (responses[1]) {
              setCrossSellSlots(responses[1].data)
          } else {
              setCrossSellSlots([])
          }
          setVisibleDaysCount(6) // On reset à 6 jours visibles
      })
      .finally(() => setLoading(false))
    }
  }, [watchType, watchPatientType, searchStartDate])

  // Aggrégation Doctolib-style
  const groupedSlots = availableSlots.reduce((acc, slot) => {
      if (!acc[slot.date]) acc[slot.date] = [];
      acc[slot.date].push(slot);
      return acc;
  }, {});
  
  const groupedCrossSellSlots = crossSellSlots.reduce((acc, slot) => {
      if (!acc[slot.date]) acc[slot.date] = [];
      acc[slot.date].push(slot);
      return acc;
  }, {});

  // Liste unique de toutes les dates (Principal + Alternatif)
  const allDatesSet = new Set([...Object.keys(groupedSlots), ...Object.keys(groupedCrossSellSlots)]);
  const sortedDates = Array.from(allDatesSet).sort();

  // Auto-déplier les 2 premiers jours (Uniquement s'ils ont le type PRINCIPAL demandé !)
  useEffect(() => {
      if (sortedDates.length > 0) {
          const primaryDates = sortedDates.filter(d => groupedSlots[d] && groupedSlots[d].length > 0);
          setExpandedDates(primaryDates.slice(0, 2));
      }
  }, [availableSlots, crossSellSlots])

  const toggleDate = (dateStr) => {
      if (expandedDates.includes(dateStr)) {
          setExpandedDates(expandedDates.filter(d => d !== dateStr))
      } else {
          setExpandedDates([...expandedDates, dateStr])
      }
  }

  const handleToggleCrossSell = (e, dateStr) => {
      e.stopPropagation() 
      if (showCrossSellForDate.includes(dateStr)) {
          setShowCrossSellForDate(prev => prev.filter(d => d !== dateStr))
      } else {
          setShowCrossSellForDate(prev => [...prev, dateStr])
      }
  }

  const prevStep = () => {
    setStep(s => Math.max(s - 1, STEPS.TYPE))
    window.scrollTo(0, 0)
  }

  const selectAndNext = (field, value) => {
    setValue(field, value)
    setStep(s => s + 1)
    window.scrollTo(0, 0)
  }

  const handleNextStep = async () => {
    const isValid = await triggerStepValidation()
    if (isValid) {
      setStep(s => Math.min(s + 1, STEPS.CONFIRM))
      window.scrollTo(0, 0)
    } else {
      toast.error("Veuillez vérifier les informations saisies")
    }
  }

  const triggerStepValidation = async () => {
    switch (step) {
      case STEPS.TYPE: return !!watchType
      case STEPS.PROFILE: return !!watchPatientType
      case STEPS.REASON: 
        if (watchReasonId === 'other') return !!watch('reason_other')
        return !!watchReasonId
      case STEPS.DATETIME: return !!watchDate && !!watchStartTime
      case STEPS.INFO: return await trigger(['first_name', 'last_name', 'email', 'phone', 'address'])
      default: return true
    }
  }
  
  const handleLoadMore = () => {
      if (visibleDaysCount < sortedDates.length) {
          setVisibleDaysCount(v => v + 6) // Affiche 6 jours de plus
      } else {
          // S'il n'y a plus de jours dans le buffer, on relance une recherche 30 jours plus tard !
          setSearchStartDate(addDays(searchStartDate, 30))
      }
  }

  const handleLoadPrevious = () => {
      const prevDate = subDays(searchStartDate, 30);
      const today = startOfDay(new Date());
      // Si revenir 30j en arrière nous amène avant aujourd'hui, on bloque à aujourd'hui
      setSearchStartDate(isBefore(prevDate, today) ? new Date() : prevDate);
      setVisibleDaysCount(6);
  }

  const onFinalSubmit = async (data) => {
    setLoading(true)
    try {
      const payload = { ...data }
      // Nettoyage strict pour éviter l'erreur 422 du backend
      if (payload.reason_id === 'other' || payload.reason_id === '') payload.reason_id = null
      if (payload.email === '') payload.email = null
      if (payload.notes === '') payload.notes = null
      
      const response = await api.post('/api/appointments/', payload)
      console.log("RÉPONSE API :", response.data)
      // --- LOGIQUE DE REDIRECTION ---
      if (response.data.payment_redirect_url) {
          console.log("Redirection Stancer vers :", response.data.payment_redirect_url)
          window.location.href = response.data.payment_redirect_url
      } else {
          // CAS 2 : Classique
          if (isPortal) {
              toast.success("Rendez-vous confirmé !")
              navigate(`/p/${token}`) // On retourne à l'accueil du portail !
          } else {
              navigate(`/confirmation/${response.data.token}`)
          }
      }
      // ---------------------------------------

    } catch (error) {
      console.error(error)
      // Petit plus UX : si l'erreur est précise, on l'affiche
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation')
      setLoading(false)
    }
  }

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault()
    setSubmittingWaitlist(true)
    try {
        await api.post('/api/appointments/waitlist', waitlistData)
        toast.success("Votre demande a bien été envoyée !")
        setWaitlistModalOpen(false)
        setWaitlistData({ first_name: '', last_name: '', phone: '', message: '' })
    } catch (error) {
        toast.error("Erreur lors de l'envoi de la demande.")
    } finally {
        setSubmittingWaitlist(false)
    }
  }

  //const weekDays = [...Array(7)].map((_, i) => addDays(currentWeekStart, i))

  // Déduction des couleurs dynamiques
  const primaryColor = profile?.landing_config?.theme?.text_color || '#7d9668'
  const lightColor = primaryColor + '15' // Version transparente (15% d'opacité) pour les fonds

  return (

    <div className="max-w-4xl mx-auto px-4 py-8">
      
      {/* Barre de progression */}
      <div className="mb-12">
        <div className="flex justify-between mb-2">
          {STEP_LABELS.map((label, i) => (
            <div 
                key={i} 
                className={`text-[10px] font-bold uppercase transition-colors ${step >= i ? '' : 'text-gray-300'}`}
                style={{ color: step >= i ? primaryColor : undefined }}
            >
              {label}
            </div>
          ))}
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${((step + 1) / 6) * 100}%`, backgroundColor: primaryColor }} />
        </div>
      </div>

      <div className="card shadow-xl border-sage-100 p-6 md:p-10">
        <form onSubmit={(e) => e.preventDefault()}>
          
          {/* Étape 1: Mode */}
          {step === STEPS.TYPE && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <h2 className="text-3xl font-serif  text-center" style={{ color: primaryColor }}>Choisissez le lieu</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { value: 'cabinet', label: 'Cabinet', icon: Building, desc: profile.cabinet_city || 'Au Cabinet' },
                  { value: 'video', label: 'En Vidéo', icon: Video, desc: 'Lien par email' },
                  { value: 'domicile', label: 'À Domicile', icon: Home, desc: 'Veuillez me contacter par téléphone pour la prise de RDV' }
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => selectAndNext('consultation_type', opt.value)}
                    className="p-8 border-2 rounded-3xl flex flex-col items-center gap-4 hover:border-sage-400 hover:bg-sage-50 transition-all group border-gray-100">
                    <opt.icon className="w-10 h-10 text-sage-400 group-hover:text-sage-500" />
                    <span className="font-bold text-gray-700">{opt.label}</span>
                    <span className="text-xs text-gray-400">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Étape 2: Profil */}
          {step === STEPS.PROFILE && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 text-center">
              <h2 className="text-3xl font-serif" style={{ color: primaryColor }}>Votre profil</h2>
              <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <button type="button" onClick={() => selectAndNext('patient_type', 'new')}
                  className="p-8 border-2 border-gray-100 rounded-3xl hover:border-sage-400 hover:bg-sage-50 transition-all">
                  <p className="font-bold text-xl text-gray-700 mb-2">Première consultation</p>
                  <p className="text-sm text-gray-500"></p>
                </button>
                <button type="button" onClick={() => selectAndNext('patient_type', 'follow_up')}
                  className="p-8 border-2 border-gray-100 rounded-3xl hover:border-sage-400 hover:bg-sage-50 transition-all">
                  <p className="font-bold text-xl text-gray-700 mb-2">Consultation de suivi</p>
                  <p className="text-sm text-gray-500"></p>
                </button>
              </div>
            </div>
          )}

          {/* Étape 3: Motif */}
          {step === STEPS.REASON && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <h2 className="text-3xl font-serif text-center" style={{ color: primaryColor }} >Quel est le motif ?</h2>
              <div className="max-w-md mx-auto space-y-4">
                <select 
                  {...register('reason_id')} 
                  className="w-full p-4 rounded-2xl border-2 border-gray-100 focus:border-sage-400 outline-none"
                  onChange={(e) => {
                    const val = e.target.value
                    setValue('reason_id', val) // FORCE LA MISE A JOUR DU WATCH
                    if(val !== '' && val !== 'other') {
                      setStep(s => s + 1)
                      window.scrollTo(0, 0)
                    }
                  }}
                >
                  <option value="">Sélectionnez un motif...</option>
                  {reasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  <option value="other">Autre motif précis</option>
                </select>
                {watchReasonId === 'other' && (
                  <div className="flex gap-2 animate-in zoom-in-95">
                    <input type="text" placeholder="Précisez votre motif..." {...register('reason_other', { required: true })} 
                      className="flex-1 p-4 rounded-2xl border-2 border-gray-100 focus:border-sage-400 outline-none" />
                    <button type="button" onClick={handleNextStep} className="text-white p-4 rounded-2xl shadow-lg transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}><ChevronRight /></button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Étape 4: Date/Heure (Smart Calendar Doctolib-Style) */}
          {step === STEPS.DATETIME && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              <h2 className="text-3xl font-serif text-center mb-8" style={{ color: primaryColor }} >Choisissez la date</h2>
              
              {loading && availableSlots.length === 0 ? (
                  <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : (
                  <div className="max-w-xl mx-auto space-y-4">
                      {sortedDates.length === 0 ? (
                          <div className="text-center py-12 bg-sage-50 rounded-2xl border-2 border-sage-100">
                              <CalendarIcon className="w-12 h-12 mx-auto text-sage-300 mb-3" />
                              <p className="text-sage-600 font-medium">Aucun créneau disponible sur cette période.</p>
                          </div>
                      ) : (
                          sortedDates.slice(0, visibleDaysCount).map((dateStr) => {
                              const daySlots = groupedSlots[dateStr] ||[]
                              const dayCrossSellSlots = groupedCrossSellSlots[dateStr] ||[]
                              const dateObj = new Date(dateStr)
                              
                              const isExpanded = expandedDates.includes(dateStr)
                              const isCrossSellExpanded = showCrossSellForDate.includes(dateStr)
                              
                              const isCrossSellOnly = daySlots.length === 0 && dayCrossSellSlots.length > 0
                              
                              // Thème dynamique pour le cross-selling
                              const crossType = watchType === 'cabinet' ? 'video' : (watchType === 'video' ? 'cabinet' : null);
                              const csTheme = crossType === 'video' 
                                  ? { text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', btnBorder: 'border-purple-200', btnHover: 'hover:border-purple-500 hover:text-purple-700', icon: Video, label: 'Vidéo' }
                                  : { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', btnBorder: 'border-emerald-200', btnHover: 'hover:border-emerald-500 hover:text-emerald-700', icon: Building, label: 'Cabinet' };
                              const CsIcon = csTheme.icon;

                              return (
                                  <div key={dateStr} className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-sage-300 transition-colors shadow-sm">
                                      <div 
                                          className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                                          onClick={() => toggleDate(dateStr)}
                                      >
                                          <h3 className="font-bold text-gray-700 capitalize">
                                              {format(dateObj, 'EEEE d MMMM', { locale: fr })}
                                          </h3>
                                          <div className="flex items-center gap-3">
                                              {!isExpanded && (
                                                  isCrossSellOnly ? (
                                                      <span className={`text-[10px] font-bold ${csTheme.text} ${csTheme.bg} px-2 py-1 rounded-full border ${csTheme.border} flex items-center gap-1`}>
                                                          <CsIcon className="w-3 h-3" /> {dayCrossSellSlots.length} dispo{dayCrossSellSlots.length > 1 ? 's' : ''} ({csTheme.label})
                                                      </span>
                                                  ) : (
                                                      <span className="text-xs font-bold text-sage-600 bg-sage-50 px-2 py-1 rounded-full border border-sage-200">
                                                          {daySlots.length} dispo{daySlots.length > 1 ? 's' : ''}
                                                      </span>
                                                  )
                                              )}
                                              {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                                          </div>
                                      </div>
                                      
                                      {/* Contenu de l'accordéon */}
                                      {isExpanded && (
                                          <div className="p-5 border-t border-gray-100 animate-in fade-in slide-in-from-top-2">
                                              
                                              {/* Slots Principaux */}
                                              {daySlots.length > 0 && (
                                                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                                                      {daySlots.map((slot, idx) => (
                                                          <button 
                                                              key={idx} 
                                                              type="button"
                                                              onClick={() => { setValue('consultation_type', slot.consultation_type); setValue('date', slot.date); selectAndNext('start_time', slot.start_time); }}
                                                              className="py-2.5 text-sm font-bold rounded-xl border-2 border-sage-100 text-sage-700 hover:border-sage-400 hover:bg-sage-50 hover:-translate-y-0.5 transition-all bg-white shadow-sm"
                                                          >
                                                              {slot.start_time.slice(0, 5)}
                                                          </button>
                                                      ))}
                                                  </div>
                                              )}

                                              {/* Encart Cross-Selling (Mixte) */}
                                              {!isCrossSellOnly && dayCrossSellSlots.length > 0 && (
                                                  <div className="mt-4 pt-4 border-t border-gray-100">
                                                      {!isCrossSellExpanded ? (
                                                          <button 
                                                              type="button"
                                                              onClick={(e) => handleToggleCrossSell(e, dateStr)} 
                                                              className={`w-full py-3 ${csTheme.bg} hover:opacity-80 ${csTheme.text} text-sm font-bold rounded-xl border ${csTheme.border} transition-colors flex items-center justify-center gap-2 shadow-sm`}
                                                          >
                                                              <CsIcon className="w-4 h-4" />
                                                              {dayCrossSellSlots.length} créneau{dayCrossSellSlots.length > 1 ? 'x' : ''} en {csTheme.label.toLowerCase()} disponible{dayCrossSellSlots.length > 1 ? 's' : ''} ce jour (Afficher)
                                                          </button>
                                                      ) : (
                                                          <div className={`p-4 ${csTheme.bg} rounded-xl border ${csTheme.border} animate-in fade-in zoom-in-95`}>
                                                              <div className="flex items-center justify-between mb-3">
                                                                  <span className={`text-xs font-bold ${csTheme.text} uppercase flex items-center gap-1.5`}><CsIcon className="w-4 h-4" /> Consultations {csTheme.label}</span>
                                                                  <button type="button" onClick={(e) => handleToggleCrossSell(e, dateStr)} className={`text-gray-400 hover:${csTheme.text} bg-white p-1 rounded-full`}><X className="w-4 h-4"/></button>
                                                              </div>
                                                              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                                                                  {dayCrossSellSlots.map((slot, idx) => (
                                                                      <button
                                                                          key={`cs-${idx}`}
                                                                          type="button"
                                                                          onClick={() => {
                                                                              setValue('consultation_type', crossType);
                                                                              setValue('date', slot.date);
                                                                              selectAndNext('start_time', slot.start_time);
                                                                          }}
                                                                          className={`py-2.5 text-sm font-bold rounded-xl border-2 ${csTheme.btnBorder} ${csTheme.text} ${csTheme.btnHover} hover:bg-white hover:-translate-y-0.5 transition-all bg-white shadow-sm`}
                                                                      >
                                                                          {slot.start_time.slice(0, 5)}
                                                                      </button>
                                                                  ))}
                                                              </div>
                                                          </div>
                                                      )}
                                                  </div>
                                              )}

                                              {/* Affichage 100% Alternatif */}
                                              {isCrossSellOnly && (
                                                  <div className={`p-4 ${csTheme.bg} rounded-xl border ${csTheme.border}`}>
                                                      <div className="flex items-center mb-4">
                                                          <span className={`text-xs font-bold ${csTheme.text} uppercase flex items-center gap-1.5`}>
                                                              <CsIcon className="w-4 h-4" /> Consultations en {csTheme.label.toLowerCase()} uniquement
                                                          </span>
                                                      </div>
                                                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                                                          {dayCrossSellSlots.map((slot, idx) => (
                                                              <button
                                                                  key={`cso-${idx}`}
                                                                  type="button"
                                                                  onClick={() => {
                                                                      setValue('consultation_type', crossType);
                                                                      setValue('date', slot.date);
                                                                      selectAndNext('start_time', slot.start_time);
                                                                  }}
                                                                  className={`py-2.5 text-sm font-bold rounded-xl border-2 ${csTheme.btnBorder} ${csTheme.text} ${csTheme.btnHover} hover:bg-white hover:-translate-y-0.5 transition-all bg-white shadow-sm`}
                                                              >
                                                                  {slot.start_time.slice(0, 5)}
                                                              </button>
                                                          ))}
                                                      </div>
                                                  </div>
                                              )}

                                          </div>
                                      )}
                                  </div>
                              )
                          })
                      )}

                      {/* Boutons de Navigation (Précédent / Suivant) */}
                      <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                          {isAfter(startOfDay(searchStartDate), startOfDay(new Date())) && (
                              <button 
                                  type="button" 
                                  onClick={handleLoadPrevious} 
                                  disabled={loading}
                                  className="btn bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50 font-bold flex justify-center items-center gap-2 w-full sm:w-auto px-6"
                              >
                                  <ChevronLeft className="w-4 h-4" /> Précédent
                              </button>
                          )}
                          <button 
                              type="button" 
                              onClick={handleLoadMore} 
                              disabled={loading}
                              className="btn bg-white border-2 border-sage-200 text-sage-600 hover:bg-sage-50 font-bold flex justify-center items-center gap-2 w-full sm:w-auto px-6"
                          >
                              {loading ? <div className="spinner w-4 h-4" /> : "Voir plus de dates"}
                          </button>
                      </div>

                      {/* NOUVEAU : BOUTON LISTE D'ATTENTE (Contrôlé par l'admin) */}
                      {publicSettings.waitlist_enabled && (
                          <div className="mt-8 pt-8 border-t border-gray-100 text-center animate-in fade-in">
                              <p className="text-gray-500 mb-4">Aucun créneau ne correspond à vos disponibilités ?</p>
                              <button 
                                  type="button" 
                                  onClick={() => setWaitlistModalOpen(true)} 
                                  className="btn transition-all font-bold w-full sm:w-auto hover:opacity-80"
                                  style={{ backgroundColor: lightColor, color: primaryColor }}
                              >
                                  Faire une demande exceptionnelle
                              </button>
                          </div>
                      )}
                  </div>
              )}
            </div>
          )}

          {/* Étape 5: Infos */}
          {step === STEPS.INFO && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 max-w-xl mx-auto">
              <h2 className="text-3xl font-serif text-center" style={{ color: primaryColor }} >Vos coordonnées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Prénom</label>
                  <input {...register('first_name', { required: true })} className={`w-full p-4 rounded-2xl bg-gray-50 border-2 ${errors.first_name ? 'border-red-200 bg-red-50' : 'border-transparent'}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Nom</label>
                  <input {...register('last_name', { required: true })} className={`w-full p-4 rounded-2xl bg-gray-50 border-2 ${errors.last_name ? 'border-red-200 bg-red-50' : 'border-transparent'}`} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Email</label>
                <input type="email" {...register('email', { required: true, pattern: EMAIL_REGEX })} className={`w-full p-4 rounded-2xl bg-gray-50 border-2 ${errors.email ? 'border-red-200 bg-red-50' : 'border-transparent'}`} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Téléphone Mobile</label>
                <input type="tel" {...register('phone', { required: true, pattern: PHONE_REGEX })} className={`w-full p-4 rounded-2xl bg-gray-50 border-2 ${errors.phone ? 'border-red-200 bg-red-50' : 'border-transparent'}`} />
              </div>
              {watchType === 'domicile' && (
                 <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Adresse de visite</label>
                    <textarea {...register('address', { required: true })} className={`w-full p-4 rounded-2xl bg-gray-50 border-2 ${errors.address ? 'border-red-200 bg-red-50' : 'border-transparent'}`} rows={2} />
                 </div>
              )}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 ml-2 uppercase">Notes (optionnel)</label>
                <textarea {...register('notes')} className="w-full p-4 rounded-2xl bg-gray-50 border-transparent border-2" rows={2} />
              </div>
              <button type="button" onClick={handleNextStep} className="w-full py-5 text-white rounded-2xl font-bold shadow-lg transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>Continuer</button>
            </div>
          )}

          {/* Étape 6: Confirm */}
          {step === STEPS.CONFIRM && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 text-center max-w-lg mx-auto">
              <h2 className="text-3xl font-serif" style={{ color: primaryColor }}>Dernière vérification</h2>
              <div className="bg-sage-50 p-8 rounded-3xl text-left space-y-4 border-2 border-sage-100">
                <div className="flex justify-between border-b border-sage-200 pb-2">
                  <span className="text-gray-400 text-sm">Date & Heure</span>
                  <span className="font-bold text-sage-800">{format(new Date(watchDate), 'dd MMMM yyyy', { locale: fr })} à {watchStartTime.slice(0, 5)}</span>
                </div>
                <div className="flex justify-between border-b border-sage-200 pb-2">
                  <span className="text-gray-400 text-sm">Lieu</span>
                  <span className="font-bold text-sage-800 capitalize">{watchType}</span>
                </div>
                <div className="flex justify-between border-b border-sage-200 pb-2">
                  <span className="text-gray-400 text-sm">Patient</span>
                  <span className="font-bold text-sage-800">{watch('first_name')} {watch('last_name')}</span>
                </div>
              </div>
              <button type="button" onClick={handleSubmit(onFinalSubmit)} disabled={loading} className="w-full py-5 text-white rounded-2xl font-bold text-xl shadow-xl disabled:opacity-50 transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                {loading ? 'Confirmation en cours...' : 'Confirmer le RDV'}
              </button>
            </div>
          )}

          {/* Navigation Retour (VISIBLE SUR TOUTES LES PAGES SAUF LA 1ERE) */}
          {step > 0 && (
            <div className="mt-12 pt-6 border-t border-gray-100 flex justify-start">
              <button type="button" onClick={prevStep} className="flex items-center gap-2 text-sage-500 font-bold hover:text-sage-700 transition-all">
                <ChevronLeft className="w-5 h-5" /> Retour
              </button>
            </div>
          )}
        </form>
      </div>

      {/* MODALE LISTE D'ATTENTE */}
      {waitlistModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 animate-in zoom-in-95">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-2xl font-serif text-sage-800">Demande exceptionnelle</h3>
                      <button onClick={() => setWaitlistModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                  </div>
                  <p className="text-sm text-sage-600 mb-6">Laissez vos coordonnées et vos préférences (jours, heures). {profile.admin_name ? profile.admin_name.split(' ')[0] : 'Le praticien'} vous recontactera si un créneau se libère.</p>
                  
                  <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                          <input required type="text" placeholder="Prénom" value={waitlistData.first_name} onChange={e => setWaitlistData({...waitlistData, first_name: e.target.value})} className="w-full p-3 rounded-xl border-gray-200 bg-gray-50 focus:bg-white" />
                          <input required type="text" placeholder="Nom" value={waitlistData.last_name} onChange={e => setWaitlistData({...waitlistData, last_name: e.target.value})} className="w-full p-3 rounded-xl border-gray-200 bg-gray-50 focus:bg-white" />
                      </div>
                      <input required type="tel" placeholder="Téléphone mobile" value={waitlistData.phone} onChange={e => setWaitlistData({...waitlistData, phone: e.target.value})} className="w-full p-3 rounded-xl border-gray-200 bg-gray-50 focus:bg-white" />
                      <textarea required rows="3" placeholder="Vos disponibilités idéales (Ex: Le mardi soir après 18h, ou le jeudi matin...). Indiquez également le motif de votre demande" value={waitlistData.message} onChange={e => setWaitlistData({...waitlistData, message: e.target.value})} className="w-full p-3 rounded-xl border-gray-200 bg-gray-50 focus:bg-white resize-none" />
                      
                      <button type="submit" disabled={submittingWaitlist} className="w-full btn py-4 text-lg mt-2 text-white transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                          {submittingWaitlist ? 'Envoi en cours...' : 'Envoyer ma demande'}
                      </button>
                  </form>
              </div>
          </div>
      )}

    </div>
  )
}